class PouchSequence extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._waitForGsap();
  }

  disconnectedCallback() {
    if (this._st) this._st.kill();
    if (this._resizeId) clearTimeout(this._resizeId);
    if (this._canvas && this._canvas.parentNode === document.body) {
      document.body.removeChild(this._canvas);
    }
  }

  _waitForGsap() {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      this._init();
    } else {
      requestAnimationFrame(() => this._waitForGsap());
    }
  }

  _init() {
    gsap.registerPlugin(ScrollTrigger);

    this._isMobile = window.innerWidth < 1024;
    this._canvas = this.querySelector('canvas');
    this._ctx = this._canvas.getContext('2d');
    this._dpr = Math.min(window.devicePixelRatio || 1, 2);
    this._frameCount = parseInt(this.dataset.frameCount, 10) || 90;
    this._frameUrlTemplate = this._isMobile
      ? (this.dataset.frameUrlMobile || this.dataset.frameUrl)
      : this.dataset.frameUrl;
    this._images = new Array(this._frameCount);
    this._currentFrame = -1;
    this._docked = false;
    this._loadedCount = 0;
    this._zIndex = '15';

    this._hero = document.querySelector('[data-section-hero]');
    this._landing = this._isMobile
      ? document.querySelector('[data-pouch-landing-mobile]')
      : document.querySelector('[data-pouch-landing]');
    if (!this._hero || !this._landing) return;

    document.body.appendChild(this._canvas);
    this._canvas.style.position = 'fixed';
    this._canvas.style.left = '0';
    this._canvas.style.top = '0';
    this._canvas.style.zIndex = this._zIndex;
    this._canvas.style.willChange = 'transform';
    this._canvas.style.pointerEvents = 'none';
    this._canvas.style.opacity = '0';

    this._computePositions();
    this._loadFrames();
    this._buildTimeline();

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeId);
      this._resizeId = setTimeout(() => this._onResize(), 200);
    });
  }

  _computePositions() {
    const scrollY = window.scrollY;
    const landingRect = this._landing.getBoundingClientRect();
    const heroRect = this._hero.getBoundingClientRect();

    const aspect = 1200 / 1500;

    if (this._isMobile) {
      const fitW = Math.min(window.innerWidth * 0.6, 280);
      const fitH = fitW / aspect;

      this._pouchW = fitW;
      this._pouchH = fitH;

      this._canvas.width = Math.round(fitW * this._dpr);
      this._canvas.height = Math.round(fitH * this._dpr);
      this._canvas.style.width = fitW + 'px';
      this._canvas.style.height = fitH + 'px';

      const startX = (window.innerWidth - fitW) * 0.5;
      const heroBottom = heroRect.top + scrollY + heroRect.height;
      const startY = heroBottom - fitH * 0.5 - 36;

      const endX = landingRect.left + (landingRect.width - fitW) * 0.5;
      const endY = landingRect.top + scrollY + (landingRect.height - fitH) * 0.5;

      this._startDocX = startX;
      this._startDocY = startY;
      this._endDocX = endX;
      this._endDocY = endY;
    } else {
      const fitH = landingRect.height * 0.9;
      const fitW = fitH * aspect;

      this._pouchW = fitW;
      this._pouchH = fitH;

      this._canvas.width = Math.round(fitW * this._dpr);
      this._canvas.height = Math.round(fitH * this._dpr);
      this._canvas.style.width = fitW + 'px';
      this._canvas.style.height = fitH + 'px';

      const offsetX = landingRect.left + (landingRect.width - fitW) * 0.5;

      this._startDocX = offsetX;
      this._startDocY = heroRect.top + scrollY + heroRect.height * 0.5 - fitH * 0.5;

      this._endDocX = offsetX;
      this._endDocY = landingRect.top + scrollY + (landingRect.height - fitH) * 0.5 - 20;
    }
  }

  _frameUrl(n) {
    return this._frameUrlTemplate.replace('{N}', n);
  }

  _loadFrames() {
    const onLoad = () => {
      this._loadedCount++;
      if (this._loadedCount === 1 && this._currentFrame < 0) {
        const p = this._st ? this._st.progress : 0;
        this._currentFrame = Math.min(Math.round(p * (this._frameCount - 1)), this._frameCount - 1);
        this._drawFrame(this._currentFrame);
        if (p >= 1) {
          this._dock();
          this._canvas.style.opacity = '1';
        } else {
          this._positionCanvas(p);
          var anim = this._canvas.animate([
            { opacity: 0, transform: this._canvas.style.transform + ' scale(0.92)' },
            { opacity: 1, transform: this._canvas.style.transform + ' scale(1)' }
          ], { duration: 600, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
          anim.onfinish = () => { this._canvas.style.opacity = '1'; anim.cancel(); };
        }
      }
    };

    const loadFrame = (n) => {
      const idx = n - 1;
      if (this._images[idx]) return;
      const img = new Image();
      img.onload = onLoad;
      img.src = this._frameUrl(n);
      this._images[idx] = img;
    };

    [1, 90].forEach(loadFrame);

    const loadKeyframes = () => {
      [10, 20, 30, 40, 50, 60, 70, 80].forEach(loadFrame);
    };

    const loadAll = () => {
      for (let n = 1; n <= this._frameCount; n++) loadFrame(n);
    };

    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(loadKeyframes, { timeout: 500 });
      requestIdleCallback(loadAll, { timeout: 1500 });
    } else {
      setTimeout(loadKeyframes, 100);
      setTimeout(loadAll, 500);
    }
  }

  _buildTimeline() {
    const hormoneSection = this._landing.closest('section') || this._landing.closest('[class*="shopify-section"]');

    const heroInitialTop = Math.max(0, Math.round(this._hero.getBoundingClientRect().top));

    this._st = ScrollTrigger.create({
      trigger: this._hero,
      start: `top top+=${heroInitialTop}`,
      endTrigger: hormoneSection,
      end: 'center center',
      scrub: 0.3,
      onUpdate: (self) => {
        if (this._docked) this._undock();
        const p = self.progress;
        const frameIndex = Math.min(Math.round(p * (this._frameCount - 1)), this._frameCount - 1);
        if (frameIndex !== this._currentFrame) {
          this._currentFrame = frameIndex;
          this._drawFrame(frameIndex);
        }
        this._positionCanvas(p);
      },
      onLeave: () => this._dock(),
      onEnterBack: () => this._undock()
    });
  }

  _positionCanvas(progress) {
    if (this._docked) return;
    const docX = this._startDocX + (this._endDocX - this._startDocX) * progress;
    const docY = this._startDocY + (this._endDocY - this._startDocY) * progress;
    const vpX = docX;
    const vpY = docY - window.scrollY;
    this._canvas.style.transform = `translate3d(${vpX}px, ${vpY}px, 0)`;
  }

  _dock() {
    if (this._docked) return;
    this._docked = true;

    const landingRect = this._landing.getBoundingClientRect();
    const left = (landingRect.width - this._pouchW) * 0.5;
    const top = this._isMobile
      ? (landingRect.height - this._pouchH) * 0.5
      : (landingRect.height - this._pouchH) * 0.5 - 20;

    this._landing.appendChild(this._canvas);
    this._canvas.style.position = 'absolute';
    this._canvas.style.left = left + 'px';
    this._canvas.style.top = top + 'px';
    this._canvas.style.transform = 'none';
    this._canvas.style.zIndex = '';
    this._canvas.style.willChange = '';
  }

  _undock() {
    if (!this._docked) return;
    this._docked = false;

    document.body.appendChild(this._canvas);
    this._canvas.style.position = 'fixed';
    this._canvas.style.left = '0';
    this._canvas.style.top = '0';
    this._canvas.style.zIndex = this._zIndex;
    this._canvas.style.willChange = 'transform';
    this._canvas.style.pointerEvents = 'none';
  }

  _drawFrame(index) {
    const img = this._images[index];
    if (img && img.complete && img.naturalWidth > 0) {
      this._renderImage(img);
      return;
    }
    const nearest = this._findNearestLoaded(index);
    if (nearest !== null) this._renderImage(this._images[nearest]);
  }

  _renderImage(img) {
    const w = this._canvas.width;
    const h = this._canvas.height;
    this._ctx.clearRect(0, 0, w, h);
    this._ctx.drawImage(img, 0, 0, w, h);
  }

  _findNearestLoaded(target) {
    for (let d = 0; d <= this._frameCount; d++) {
      const lo = target - d;
      const hi = target + d;
      if (lo >= 0 && this._images[lo] && this._images[lo].complete && this._images[lo].naturalWidth > 0) return lo;
      if (hi < this._frameCount && this._images[hi] && this._images[hi].complete && this._images[hi].naturalWidth > 0) return hi;
    }
    return null;
  }

  _onResize() {
    const nowMobile = window.innerWidth < 1024;
    if (nowMobile !== this._isMobile) return;

    const wasDocked = this._docked;
    if (wasDocked) this._undock();

    this._computePositions();
    this._drawFrame(this._currentFrame);
    ScrollTrigger.refresh();

    if (wasDocked) {
      this._dock();
    } else if (this._st) {
      this._positionCanvas(this._st.progress);
    }
  }
}

customElements.define('pouch-sequence', PouchSequence);
